import { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabase';
import { X, Users, Copy, CheckCircle2, Gift } from 'lucide-react';
import { Session } from '@supabase/supabase-js';

interface ReferralModalProps {
    session: Session;
    isOpen: boolean;
    onClose: () => void;
}

interface ReferralInfo {
    user_id: string;
    display_name: string;
    created_at: string;
    is_validated: boolean;
    points_earned: number;
}

export const ReferralModal = ({ session, isOpen, onClose }: ReferralModalProps) => {
    const [referralCode, setReferralCode] = useState<string>('');
    const [referrals, setReferrals] = useState<ReferralInfo[]>([]);
    const [loading, setLoading] = useState(true);
    const [copied, setCopied] = useState(false);
    const [showRules, setShowRules] = useState(false);

    useEffect(() => {
        if (isOpen) {
            fetchData();
        }
    }, [isOpen]);

    const fetchData = async () => {
        setLoading(true);
        try {
            // 1. Fetch Referral Code
            const { data: profile } = await supabase
                .from('profiles')
                .select('referral_code')
                .single();

            if (profile) setReferralCode(profile.referral_code || '');

            // 2. Fetch Referrals using previously created rpc
            const { data: refData, error } = await supabase
                .rpc('get_my_referrals');

            if (!error && refData) {
                setReferrals(refData as ReferralInfo[]);
            } else if (error) {
                console.error("Error fetching referrals", error);
            }
        } catch (error) {
            console.error("Error", error);
        } finally {
            setLoading(false);
        }
    };

    const handleCopy = () => {
        const link = `${window.location.origin}?ref=${referralCode}`;
        navigator.clipboard.writeText(link);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    if (!isOpen) return null;

    const totalEarned = referrals.reduce((sum, r) => sum + r.points_earned, 0);

    return (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-in fade-in duration-200">
            <div className="relative w-full max-w-lg max-h-[90vh] flex flex-col bg-white dark:bg-gray-900 rounded-3xl shadow-2xl border border-gray-200 dark:border-gray-800 overflow-hidden animate-in zoom-in-95 duration-200">

                {/* Header */}
                <div className="px-6 py-4 border-b border-gray-100 dark:border-gray-800 flex flex-shrink-0 items-center justify-between bg-gray-50/50 dark:bg-gray-800/50">
                    <div className="flex items-center gap-3">
                        <div className="p-2 bg-indigo-100 dark:bg-indigo-900/30 rounded-xl text-indigo-600 dark:text-indigo-400">
                            <Users className="w-5 h-5" />
                        </div>
                        <h2 className="text-lg font-bold text-gray-900 dark:text-white">Indique e Ganhe</h2>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-full transition-colors"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>

                {/* Body */}
                <div className="overflow-y-auto p-6 space-y-6">

                    {/* Header Banner */}
                    <div className="bg-gradient-to-br from-indigo-500 to-purple-600 rounded-2xl p-6 text-white text-center shadow-lg relative overflow-hidden">
                        <div className="relative z-10">
                            <h3 className="text-2xl font-black mb-2">Ganhe 8.000 pontos!</h3>
                            <p className="text-indigo-100 text-sm mb-4">
                                Convide seus amigos para o Radar da Economia. Quando eles fizerem a primeira compra ou resgate, você ganha!
                            </p>

                            {/* Copy Link Area */}
                            <div className="flex flex-col sm:flex-row gap-2 bg-black/20 p-2 rounded-xl backdrop-blur-sm border border-white/10">
                                <input
                                    type="text"
                                    readOnly
                                    value={referralCode ? `${window.location.origin}?ref=${referralCode}` : 'Carregando...'}
                                    className="flex-1 bg-transparent px-3 py-2 text-sm text-center sm:text-left text-white font-medium focus:outline-none"
                                />
                                <button
                                    onClick={handleCopy}
                                    disabled={!referralCode}
                                    className="flex items-center justify-center gap-2 px-6 py-2 bg-white text-indigo-600 rounded-lg text-sm font-bold hover:bg-indigo-50 transition-colors disabled:opacity-50"
                                >
                                    {copied ? <CheckCircle2 className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                                    {copied ? 'Copiado!' : 'Copiar'}
                                </button>
                            </div>
                        </div>

                        {/* Decorative Background */}
                        <div className="absolute -top-10 -right-10 w-32 h-32 bg-white/10 rounded-full blur-2xl"></div>
                        <div className="absolute -bottom-10 -left-10 w-32 h-32 bg-black/10 rounded-full blur-2xl"></div>
                    </div>

                    {/* Stats & Rules */}
                    <div className="grid grid-cols-2 gap-4">
                        <div className="bg-green-50 dark:bg-green-900/10 border border-green-100 dark:border-green-900/20 rounded-2xl p-4">
                            <div className="flex items-center gap-2 mb-1">
                                <Gift className="w-4 h-4 text-green-600 dark:text-green-500" />
                                <span className="text-xs font-bold text-green-600 dark:text-green-500 uppercase">Total Ganho</span>
                            </div>
                            <span className="text-xl font-black text-gray-900 dark:text-white">
                                {totalEarned.toLocaleString('pt-BR')} <span className="text-sm font-normal text-gray-500">pts</span>
                            </span>
                        </div>
                        <div className="bg-blue-50 dark:bg-blue-900/10 border border-blue-100 dark:border-blue-900/20 rounded-2xl p-4">
                            <div className="flex items-center gap-2 mb-1">
                                <Users className="w-4 h-4 text-blue-600 dark:text-blue-500" />
                                <span className="text-xs font-bold text-blue-600 dark:text-blue-500 uppercase">Indicados</span>
                            </div>
                            <span className="text-xl font-black text-gray-900 dark:text-white">
                                {referrals.length} <span className="text-sm font-normal text-gray-500">amigos</span>
                            </span>
                        </div>
                    </div>

                    {/* Rules Toggle */}
                    <div className="bg-gray-50 dark:bg-gray-800/30 rounded-2xl border border-gray-100 dark:border-gray-800 overflow-hidden transition-all duration-300">
                        <button
                            onClick={() => setShowRules(!showRules)}
                            className="w-full flex items-center justify-between p-4 text-sm font-bold text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800/50 transition-colors"
                        >
                            <span className="flex items-center gap-2">
                                <span className="p-1 rounded bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400">
                                    ℹ️
                                </span>
                                Como funciona a indicação?
                            </span>
                            <span className={`transform transition-transform duration-300 ${showRules ? 'rotate-180' : ''}`}>
                                ▼
                            </span>
                        </button>

                        <div className={`transition-all duration-300 ease-in-out ${showRules ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0 overflow-hidden'}`}>
                            <div className="p-4 pt-0 text-sm text-gray-600 dark:text-gray-400 space-y-3">
                                <p>Veja como é simples ganhar pontos indicando o Radar da Economia:</p>
                                <ol className="list-decimal list-inside space-y-2 ml-1">
                                    <li>Copie seu link único e envie para quem quiser.</li>
                                    <li>Seu amigo(a) clica, e faz o cadastro na plataforma pelo seu link.</li>
                                    <li><strong>O prêmio:</strong> Você ganha <strong>8.000 pontos</strong> automaticamente quando a pessoa indicada tiver a primeira aprovação no Radar, seja um pedido de pontos por compra ou um resgate na Lojinha.</li>
                                </ol>
                                <div className="mt-3 p-3 bg-amber-50 dark:bg-amber-900/10 text-amber-700 dark:text-amber-500 rounded-xl text-xs border border-amber-100 dark:border-amber-900/20">
                                    <strong>Importante:</strong> Você não ganha os pontos só pelo envio do link ou cadastro do seu amigo. É preciso que ele use o Radar pra valer para destravar a sua recompensa.
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="pt-2">
                        <h4 className="text-sm font-bold text-gray-900 dark:text-white mb-4">Meus Amigos</h4>

                        {loading ? (
                            <div className="space-y-3">
                                {[1, 2, 3].map(i => (
                                    <div key={i} className="h-14 bg-gray-100 dark:bg-gray-800/50 rounded-xl animate-pulse" />
                                ))}
                            </div>
                        ) : referrals.length === 0 ? (
                            <div className="text-center py-6 px-4 bg-gray-50 dark:bg-gray-800/30 rounded-2xl border border-gray-100 dark:border-gray-800">
                                <Users className="w-8 h-8 text-gray-300 dark:text-gray-600 mx-auto mb-2" />
                                <p className="text-sm text-gray-500 dark:text-gray-400">
                                    Ninguém usou seu código ainda.
                                    <br />Compartilhe e comece a ganhar!
                                </p>
                            </div>
                        ) : (
                            <div className="space-y-3 max-h-48 overflow-y-auto pr-2 custom-scrollbar">
                                {referrals.map((ref) => (
                                    <div key={ref.user_id} className="flex items-center justify-between p-3 bg-white dark:bg-gray-900 border border-gray-100 dark:border-gray-800 rounded-xl shadow-sm">
                                        <div>
                                            <p className="font-semibold text-sm text-gray-900 dark:text-white">
                                                {ref.display_name}
                                            </p>
                                            <p className="text-xs text-gray-500">
                                                Em {new Date(ref.created_at).toLocaleDateString('pt-BR')}
                                            </p>
                                        </div>
                                        <div className="text-right">
                                            {ref.is_validated ? (
                                                <span className="inline-flex items-center justify-center px-2 py-1 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 text-xs font-bold rounded-lg gap-1">
                                                    +{ref.points_earned} pts
                                                </span>
                                            ) : (
                                                <span className="text-xs font-medium text-amber-600 dark:text-amber-500 bg-amber-50 dark:bg-amber-900/10 px-2 py-1 rounded-lg">
                                                    Pendente
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};
